import axios from "axios";
import moment from "moment-timezone";

const LAMBDA_LOGGER_URL = process.env.REACT_APP_LAMBDA_LOGGER_URL ||
  "https://hrf23n4nbnatueqfujaywxjazq0tlatq.lambda-url.ap-south-1.on.aws/";

const IP_LOOKUP_TIMEOUT_MS = 1500;
// How long a background log waits for the IP before going out with ipAddress
// "unknown"; the lookup carries on for the next logs.
const IP_WAIT_MS = 500;
const CLIENT_IP_KEY = "cy_client_ip";
let clientIpPromise = null;

export const isLambdaLoggingEnabled = () =>
  (process.env.REACT_APP_ENABLE_LAMBDA_LOGS || "disabled") !== "disabled";

// One IP lookup per tab (kept in sessionStorage), shared by every log — as in
// epicenter-new's lambdaLogger. A slow or blocked lookup gives up after
// IP_LOOKUP_TIMEOUT_MS so it never holds a log back.
const getClientIp = () => {
  if (!clientIpPromise) {
    let stored = null;
    try {
      stored = sessionStorage.getItem(CLIENT_IP_KEY);
    } catch (_) {
      /* storage blocked */
    }
    clientIpPromise = stored
      ? Promise.resolve(stored)
      : axios
          .get("https://api.ipify.org?format=json", { timeout: IP_LOOKUP_TIMEOUT_MS })
          .then((res) => {
            const ip = res?.data?.ip || "unknown";
            try {
              if (ip !== "unknown") sessionStorage.setItem(CLIENT_IP_KEY, ip);
            } catch (_) {
              /* storage blocked */
            }
            return ip;
          })
          .catch(() => {
            clientIpPromise = null;
            return "unknown";
          });
  }
  return clientIpPromise;
};

export const warmUpClientIp = () => {
  getClientIp();
};

/**
 * Sends one log entry to the logging Lambda (MongoDB). Never throws.
 *
 * Off unless REACT_APP_ENABLE_LAMBDA_LOGS is set at build time (an Amplify
 * env var, set per branch): while off, the entry is only printed to the
 * console, which is how staging events are checked without writing them.
 */
export const logToLambda = async (logData) => {
  try {
    if (!isLambdaLoggingEnabled()) {
      console.log("Lambda logging disabled:", logData);
      return;
    }

    const ipAddress = await Promise.race([
      getClientIp(),
      new Promise((resolve) => setTimeout(() => resolve("unknown"), IP_WAIT_MS)),
    ]);

    const requestBody = {
      timestamp: new Date().toISOString(),
      environment: getEventEnv(),
      ...logData,
      ipAddress,
    };

    const response = await axios.post(LAMBDA_LOGGER_URL, requestBody, {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "Content-Type",
        "Access-Control-Allow-Methods": "OPTIONS,POST,GET",
      }
    });

    return response.data;
  } catch (error) {
  }
};

export const createRescheduleLog = ({
  event_name,
  endpoint,
  studentId,
  scheduleId,
  requestBody,
  response,
  error,
}) => {
  const istDate = moment().tz("Asia/Kolkata").format("YYYY-MM-DD HH:mm:ss");
  return {
    event_name,
    event_env: getEventEnv(),
    event_date_ist: istDate,
    endpoint,
    studentId,
    scheduleId,
    requestBody: JSON.stringify(requestBody),
    response: response ? JSON.stringify(response) : null,
    error: error ? JSON.stringify(error) : null,
    success: !error,
  };
};

// The IP already looked up in this tab, or "unknown" — never waits.
const storedClientIp = () => {
  try {
    return sessionStorage.getItem(CLIENT_IP_KEY) || "unknown";
  } catch (_) {
    return "unknown";
  }
};

/**
 * Sends one log entry right before the page navigates away (logout, profile
 * switch), without the caller waiting for it: a keepalive request is finished
 * by the browser even after the page has gone (same as logoutUser's cy-sso
 * api/logout call). Sent as text/plain so the
 * browser makes no CORS preflight (which a page being left can't wait for);
 * the Lambda parses the body as JSON either way. Never throws.
 */
export const sendLogOnLeave = (logData) => {
  try {
    if (!isLambdaLoggingEnabled()) {
      console.log("Lambda logging disabled:", logData);
      return;
    }

    const body = JSON.stringify({
      timestamp: new Date().toISOString(),
      environment: getEventEnv(),
      ...logData,
      ipAddress: storedClientIp(),
    });

    fetch(LAMBDA_LOGGER_URL, {
      method: "POST",
      keepalive: true,
      credentials: "omit",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body,
    }).catch(() => {});
  } catch (_) {
    /* never block a logout */
  }
};

// local / staging / production: REACT_APP_ENVIRONMENT, set per branch in the
// deploy env UI (the same variable the feature flags read). Every log's
// event_env and environment use it, so one query covers every log type.
export const getEventEnv = () => process.env.REACT_APP_ENVIRONMENT || "local";

export const createAuthLog = ({ eventName, ...fields }) => ({
  event_name: eventName,
  event_env: getEventEnv(),
  event_date_ist: moment().tz("Asia/Kolkata").format("YYYY-MM-DD HH:mm:ss"),
  ...fields,
});
